import React from 'react'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { GOLD_ITEM_TYPES } from '../lib/goldItemTypes.mjs'

// Full karat options 9-24
const KARATS = [24, 23, 22, 21, 20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9]

// Decimal options for karat
const DECIMALS = ['00', '10', '20', '30', '40', '50', '60', '70', '80', '90']

// text-base on mobile (sub-16px focused controls trigger iOS auto-zoom);
// h-11 gives the 44px finger target at the counter.
const CONTROL_CLASSES =
  'h-11 md:h-10 px-3 border border-[#DCDFE5] rounded-lg text-base md:text-sm font-medium text-[#17191D] bg-white transition-colors hover:border-[#B6BDC8] focus:outline-none focus:ring-2 focus:ring-[#2457DB]/15 focus:border-[#2457DB] disabled:bg-[#F5F6F8]'

const MetalRowInput = ({ item, index, onUpdate, onRemove, canRemove, itemErrors = {}, itemTypeError = itemErrors.type }) => {
  const is24K = item.metal === 'gold' && item.karatWhole === '24'
  const typeId = `gold-item-type-${item.id}`
  const metalId = `gold-item-metal-${item.id}`
  const purityId = `gold-item-purity-${item.id}`
  const weightId = `gold-item-weight-${item.id}`

  // 24K is pure gold — a fractional karat above 24 would be invalid, so the
  // decimal locks to .00 (same rule as the shop app).
  const handleKaratChange = (whole) => {
    onUpdate(whole === '24' ? { karatWhole: whole, karatDecimal: '00' } : { karatWhole: whole })
  }

  const removeButton = (
    <Button
      variant="ghost"
      size="icon"
      onClick={onRemove}
      disabled={!canRemove}
      aria-label="Remove item"
      className={canRemove ? 'text-[#656C78] hover:text-[#17191D] hover:bg-[#F5F6F8]' : 'opacity-30'}
    >
      <Icon name="Trash2" size={18} />
    </Button>
  )

  return (
    // The two-column form keeps every value readable in the desktop sidebar
    // layout and on phones, with persistent labels above the touch targets.
    <div className="px-4 py-4 sm:px-5 sm:py-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-[#17191D]">Item {String(index + 1).padStart(2, '0')}</h3>
        {removeButton}
      </div>

      <div className="grid grid-cols-1 gap-x-3 gap-y-4 min-[360px]:grid-cols-2 sm:gap-x-4">
        <label className="min-w-0 text-xs font-medium text-[#656C78]">
          Metal (required)
          <select
            id={metalId}
            value={item.metal}
            onChange={(e) => onUpdate({ metal: e.target.value })}
            aria-label="Metal"
            required
            aria-invalid={itemErrors.metal ? true : undefined}
            aria-describedby={itemErrors.metal ? `${metalId}-error` : undefined}
            className={`${CONTROL_CLASSES} mt-1.5 w-full min-w-0`}
          >
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
          </select>
          {itemErrors.metal && <span id={`${metalId}-error`} role="alert" className="mt-2 block text-xs leading-5 text-destructive">{itemErrors.metal}</span>}
        </label>

        <div className="min-w-0">
          <p className="text-xs font-medium text-[#656C78]">Purity{item.metal === 'gold' ? ' (required)' : ''}</p>
          {item.metal === 'gold' && (
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <select
                id={purityId}
                value={item.karatWhole ?? ''}
                onChange={(e) => handleKaratChange(e.target.value)}
                aria-label="Karat"
                required
                aria-invalid={itemErrors.purity ? true : undefined}
                aria-describedby={itemErrors.purity ? `${purityId}-error` : undefined}
                className={`${CONTROL_CLASSES} w-full min-w-0 px-2 tabular-nums`}
              >
                <option value="" disabled>Select purity</option>
                {KARATS.map(k => (
                  <option key={k} value={k}>{k}K</option>
                ))}
              </select>
              <select
                value={is24K ? '00' : (item.karatDecimal ?? '')}
                onChange={(e) => onUpdate({ karatDecimal: e.target.value })}
                disabled={is24K}
                aria-label="Karat decimal"
                required
                aria-invalid={itemErrors.purity ? true : undefined}
                aria-describedby={itemErrors.purity ? `${purityId}-error` : undefined}
                className={`${CONTROL_CLASSES} w-full min-w-0 px-2 tabular-nums disabled:text-[#656C78]`}
              >
                <option value="" disabled>Select decimal</option>
                {DECIMALS.map(d => (
                  <option key={d} value={d}>.{d}</option>
                ))}
              </select>
            </div>
          )}
          {item.metal === 'silver' && (
            <div className="mt-1.5 flex h-11 items-center rounded-lg border border-[#DCDFE5] bg-[#F5F6F8] px-3 text-sm font-medium text-[#656C78] md:h-10">
              99% pure
            </div>
          )}
          {itemErrors.purity && <span id={`${purityId}-error`} role="alert" className="mt-2 block text-xs leading-5 text-destructive">{itemErrors.purity}</span>}
        </div>

        <label className="min-w-0 text-xs font-medium text-[#656C78]">
          Weight (required)
          <div className="relative mt-1.5">
            <input
              id={weightId}
              type="number"
              inputMode="decimal"
              step="0.001"
              min="0.001"
              required
              value={item.weight}
              onChange={(e) => onUpdate({ weight: e.target.value })}
              placeholder="Weight"
              aria-label="Weight"
              aria-invalid={itemErrors.weight ? true : undefined}
              aria-describedby={itemErrors.weight ? `${weightId}-error` : undefined}
              className={`${CONTROL_CLASSES} w-full pr-8 tabular-nums`}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#656C78]">g</span>
          </div>
          {itemErrors.weight && <span id={`${weightId}-error`} role="alert" className="mt-2 block text-xs leading-5 text-destructive">{itemErrors.weight}</span>}
        </label>

        <label className="min-w-0 text-xs font-medium text-[#656C78]">
          Item type (required)
          <select
            id={typeId}
            value={item.itemType || ''}
            onChange={(e) => onUpdate({ itemType: e.target.value })}
            aria-label="Item type"
            required
            aria-invalid={itemTypeError ? true : undefined}
            aria-describedby={itemTypeError ? `${typeId}-error` : undefined}
            className={`${CONTROL_CLASSES} mt-1.5 w-full min-w-0 ${itemTypeError ? 'border-destructive' : ''}`}
          >
            <option value="">Select type</option>
            {GOLD_ITEM_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {itemTypeError && <span id={`${typeId}-error`} role="alert" className="mt-2 block text-xs leading-5 text-destructive">{itemTypeError}</span>}
        </label>
      </div>

      <details className="mt-4 w-full border-t border-[#DCDFE5] pt-3">
        <summary className="min-h-11 cursor-pointer rounded py-3.5 text-xs font-medium text-[#656C78] hover:text-[#17191D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2457DB]">Item details (optional)</summary>
        <p className="mt-2 text-xs leading-5 text-[#656C78]">Select an item type. If you leave the description blank, the RISS report will describe the item using its recorded type, metal, karat and weight. A description entered here is kept as written. Brand, model and other details are optional.</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block text-xs font-medium text-[#656C78] sm:col-span-2">
            Description
            <textarea
              value={item.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              rows={2}
              placeholder="Optional: add distinctive details, or leave blank for an automatic description"
              className="mt-1.5 w-full rounded-lg border border-[#DCDFE5] bg-white px-3 py-2.5 text-base font-normal text-[#17191D] focus:outline-none focus:ring-2 focus:ring-[#2457DB]/15 focus:border-[#2457DB] md:text-sm"
            />
          </label>
          {[
            ['manufacturer', 'Brand / manufacturer'],
            ['model', 'Model'],
            ['serialNumber', 'Serial number'],
            ['inscription', 'Inscription / engraving'],
          ].map(([field, label]) => (
            <label key={field} className="block text-xs font-medium text-[#656C78]">
              {label}
              <input
                type="text"
                value={item[field] || ''}
                onChange={(e) => onUpdate({ [field]: e.target.value })}
                className={`${CONTROL_CLASSES} mt-1.5 w-full font-normal`}
              />
            </label>
          ))}
        </div>
      </details>
    </div>
  )
}

export default MetalRowInput
